"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"

export function EnhancedSplashScreen() {
  const [currentFrame, setCurrentFrame] = useState(0)
  const totalFrames = 4

  useEffect(() => {
    if (currentFrame < totalFrames - 1) {
      const timer = setTimeout(() => {
        setCurrentFrame((prev) => prev + 1)
      }, 1200) // Adjusted timing - not too slow, not too fast
      return () => clearTimeout(timer)
    }
  }, [currentFrame])

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-background overflow-hidden">
      {/* Title and subtitle that persist across all frames */}
      <motion.div
        className="absolute top-12 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold mb-1">
          <span className="gradient-text">Red Flag</span> Filter
        </h1>
        <p className="text-zinc-400 text-sm">AI-powered message analysis</p>
      </motion.div>

      <AnimatePresence mode="sync">
        {currentFrame === 0 && (
          <motion.div
            key="frame1"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, y: -50, transition: { duration: 0.6 } }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <motion.div
              animate={{
                rotate: [0, 10, 0, -10, 0],
                scale: [1, 1.2, 1, 1.2, 1],
              }}
              transition={{
                repeat: 1,
                duration: 1.5,
                ease: "easeInOut",
              }}
              className="text-8xl mb-6 inline-block"
            >
              🚩
            </motion.div>
          </motion.div>
        )}

        {currentFrame === 1 && (
          <motion.div
            key="frame2"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.2, y: -20, transition: { duration: 0.6 } }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="flex justify-center space-x-2 mb-4"
            >
              {["Never", "Send", "Regrettable", "Texts"].map((word, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + i * 0.1 }}
                  className="text-zinc-400 text-xl"
                >
                  {word}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>
        )}

        {currentFrame === 2 && (
          <motion.div
            key="frame3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -30, transition: { duration: 0.6 } }}
            className="text-center flex flex-col items-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-8 relative"
            >
              <motion.div
                className="absolute top-0 left-0 w-full h-full"
                animate={{
                  boxShadow: [
                    "0 0 0px rgba(244, 63, 94, 0)",
                    "0 0 30px rgba(244, 63, 94, 0.5)",
                    "0 0 0px rgba(244, 63, 94, 0)",
                  ],
                }}
                transition={{
                  repeat: Number.POSITIVE_INFINITY,
                  duration: 2,
                  ease: "easeInOut",
                }}
              />

              <motion.div className="flex items-center justify-center mb-6 relative">
                <motion.div
                  initial={{ x: -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="text-5xl mr-4"
                >
                  🚩
                </motion.div>
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                    rotate: [0, 10, 0, -10, 0],
                  }}
                  transition={{
                    delay: 0.4,
                    duration: 0.5,
                    rotate: {
                      delay: 0.9,
                      duration: 1.5,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    },
                  }}
                  className="text-5xl mx-4"
                >
                  ✨
                </motion.div>
                <motion.div
                  initial={{ x: 100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="text-5xl ml-4"
                >
                  💬
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}

        {currentFrame === 3 && (
          <motion.div
            key="frame4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center flex flex-col items-center"
          >
            <motion.div
              className="w-64 mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <div className="relative h-2">
                <motion.div
                  className="absolute top-0 left-0 h-2 bg-gradient-to-r from-rose-500 to-orange-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{
                    delay: 0.2,
                    duration: 1.2,
                    ease: "easeInOut",
                  }}
                />
              </div>

              <motion.div
                className="flex justify-between mt-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 0.5, 1] }}
                  transition={{
                    delay: 0.8,
                    duration: 1.2,
                    times: [0, 0.3, 0.6, 1],
                  }}
                  className="text-sm text-zinc-400"
                >
                  AI Powered
                </motion.p>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 0.5, 1] }}
                  transition={{
                    delay: 1.0,
                    duration: 1.2,
                    times: [0, 0.3, 0.6, 1],
                  }}
                  className="text-sm text-zinc-400"
                >
                  Message Analysis
                </motion.p>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="text-xl font-light text-zinc-300"
            >
              Let's get started
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="absolute bottom-10 left-0 right-0 flex justify-center">
        <div className="flex space-x-2">
          {[...Array(totalFrames)].map((_, i) => (
            <motion.div
              key={i}
              className={`h-2 w-2 rounded-full ${i === currentFrame ? "bg-rose-500" : "bg-zinc-700"}`}
              animate={{ scale: i === currentFrame ? [1, 1.2, 1] : 1 }}
              transition={{ repeat: i === currentFrame ? Number.POSITIVE_INFINITY : 0, duration: 1 }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

